// A single player at a time, with no video requests before an intentional tap.
if (!customElements.get('lm-course-video')) {
  customElements.define('lm-course-video', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      this.button = this.querySelector('button');
      this.error = this.querySelector('[role="status"]');
      this.button.addEventListener('click', () => this.start(), { signal: this.controller.signal });
    }
    async start() {
      if (this.pending) return;
      this.pending = true;
      this.error.hidden = true;
      this.button.setAttribute('aria-busy', 'true');
      const generation = this.generation = (this.generation || 0) + 1;
      if (!this.video) {
        this.append(this.querySelector('template').content.cloneNode(true));
        this.video = this.querySelector(':scope > video');
        this.video.setAttribute('aria-label', this.dataset.videoLabel);
        // Shopify's video_tag keeps only its largest MP4. Use the available
        // transcodes instead, without making a media request before the tap.
        const candidates = [...this.video.querySelectorAll('source')].map(source => ({ url: source.src, height: Number(source.dataset.height) || 720 }));
        const connection = navigator.connection;
        const small = connection?.saveData || /(^|slow-)2g/.test(connection?.effectiveType || '') || (connection?.downlink > 0 && connection.downlink < 1.5);
        const target = small ? 480 : 720;
        candidates.sort((a, b) => {
          const rank = height => height <= target ? target - height : 10000 + height;
          return rank(a.height) - rank(b.height);
        });
        this.sources = [...new Set(candidates.map(source => source.url))];
        this.video.querySelectorAll('source').forEach(source => source.remove());
        this.video.addEventListener('play', () => {
          document.querySelectorAll('lm-course-video video').forEach(video => {
            if (video !== this.video) video.pause();
          });
          this.classList.add('is-started');
          this.button.hidden = true;
        }, { signal: this.controller.signal });
        this.video.addEventListener('playing', () => {
          clearTimeout(this.loadTimer);
          this.error.hidden = true;
          if (!this.tracked) {
            this.tracked = true;
            window.dataLayer ||= [];
            window.dataLayer.push({ event: 'course_video_play', video_title: this.dataset.videoLabel });
          }
        }, { signal: this.controller.signal });
        this.video.addEventListener('error', () => {
          if (!this.pending) this.showError();
        }, { signal: this.controller.signal });
      }
      this.classList.add('is-started');
      this.button.hidden = true;
      this.error.classList.add('is-loading');
      this.error.textContent = this.dataset.loadingText;
      this.error.hidden = false;
      this.loadTimer = setTimeout(() => {
        if (this.isConnected && generation === this.generation && this.pending) this.error.textContent = this.dataset.slowText;
      }, 12000);
      try {
        let played = false;
        for (const url of this.sources) {
          if (!this.isConnected || generation !== this.generation) return;
          this.video.src = url;
          this.video.load();
          try { await this.video.play(); played = true; break; }
          catch (error) {
            if (!this.isConnected || generation !== this.generation) return;
            // Do not turn user/browser-paused playback into a network retry.
            if (error.name === 'NotAllowedError' || error.name === 'AbortError') throw error;
          }
        }
        if (!played) throw new Error('No playable video source');
        this.video.focus({ preventScroll: true });
      } catch {
        if (this.isConnected && generation === this.generation) { this.video.pause(); this.showError(); }
      } finally {
        if (generation === this.generation) {
          clearTimeout(this.loadTimer);
          this.pending = false;
          this.button.removeAttribute('aria-busy');
        }
      }
    }
    showError() {
      this.error.hidden = false;
      this.error.classList.remove('is-loading');
      this.error.textContent = this.dataset.errorText;
      this.button.hidden = false;
      this.classList.remove('is-started');
      this.pending = false;
      this.button.removeAttribute('aria-busy');
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      clearTimeout(this.loadTimer); this.controller?.abort();
      this.video?.pause(); this.video?.remove(); this.video = null;
      this.pending = false; this.ready = false;
      this.classList.remove('is-started');
      if (this.button) { this.button.hidden = false; this.button.removeAttribute('aria-busy'); }
      if (this.error) this.error.hidden = true;
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('lm-course-video video').forEach(video => video.pause());
  });
}
